import apiClient from './apiClient';
import type {
  TriaxialData,
  TriaxialCrossTest,
  TriaxialGeneral,
  TriaxialStage,
  TriaxialPicker,
  TriaxialUploadResult,
} from '../types/triaxial';

export const getTriaxialData = async (workOrderDetailKey: string): Promise<TriaxialData> => {
  const r = await apiClient.get<TriaxialData>(`/workorderdetails/${workOrderDetailKey}/triaxial`);
  return r.data;
};

// REQ-08 — light cross-test pull only. Used by the Parameters-tab focus refresh
// so coming back to the browser tab updates the Auto-pulled panel WITHOUT
// re-reading the full (10k-row) test payload (which caused a spinner reload).
export const getTriaxialCrossTest = async (
  workOrderDetailKey: string,
): Promise<TriaxialCrossTest | null> => {
  const r = await apiClient.get<{ crossTest: TriaxialCrossTest | null }>(
    `/workorderdetails/${workOrderDetailKey}/triaxial/cross-test`,
  );
  return r.data.crossTest;
};

export const saveTriaxialData = async (
  workOrderDetailKey: string,
  general: TriaxialGeneral,
  stages: TriaxialStage[],
): Promise<TriaxialData> => {
  const r = await apiClient.put(`/workorderdetails/${workOrderDetailKey}/triaxial`, {
    general,
    stages,
  });
  return r.data;
};

export const uploadTriaxialGds = async (
  workOrderDetailKey: string,
  file: File,
): Promise<TriaxialUploadResult> => {
  const form = new FormData();
  form.append('file', file);
  const r = await apiClient.post<TriaxialUploadResult>(
    `/workorderdetails/${workOrderDetailKey}/triaxial/upload-gds`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return r.data;
};

export const updateTriaxialPicker = async (
  workOrderDetailKey: string,
  picker: TriaxialPicker,
): Promise<TriaxialData> => {
  const r = await apiClient.patch(
    `/workorderdetails/${workOrderDetailKey}/triaxial/picker`,
    picker,
  );
  return r.data;
};

// BUG-2026-0109 — operator-selected φ'_max / φ'_end. Saved independently of
// the picker; the report prints these when set (computed φ' is the fallback).
export const updateTriaxialSelectedPhi = async (
  workOrderDetailKey: string,
  payload: { selectedPhiMaxDeg: number | null; selectedPhiEndDeg: number | null },
): Promise<TriaxialData> => {
  const r = await apiClient.patch(
    `/workorderdetails/${workOrderDetailKey}/triaxial/selected-phi`,
    payload,
  );
  return r.data;
};
